import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import type {
  SpiffPaginatedResponse,
  SpiffPagination,
} from "@/api/dto/pagination";
import type { SpiffProcessInstanceRow } from "@/api/dto/process-instance";
import {
  buildCaseReportMetadata,
  type CaseListFilters,
} from "@/api/endpoints/build-case-report-metadata";
import { qk } from "@/api/query-keys";
import type { CaseListItem } from "@/domain/case/case";
import { CASE_CONFIG, type CaseConfig } from "@/domain/case/case-config";
import { toCaseListItem } from "@/domain/case/map-process-instance";
import { refineCases } from "@/domain/case/refine-cases";

export type AllCasesParams = {
  readonly filters: CaseListFilters;
  readonly page: number;
  readonly perPage: number;
  readonly config?: CaseConfig;
};

export type AllCasesResult = {
  readonly items: readonly CaseListItem[];
  readonly pagination: SpiffPagination;
  /** クライアント側で再絞り込みしたため pagination.total が実件数より多いか。 */
  readonly isApproximateTotal: boolean;
};

const EMPTY_PAGINATION: SpiffPagination = { count: 0, total: 0, pages: 0 };

/**
 * 全案件（管理向け）。マイ案件と違い `initiated_by_me` で絞らない。
 *
 * エンドポイントも `/process-instances`（全件）で、閲覧できるかは backend の
 * 権限判定に委ねる（UI 側で判定を持たない）。
 */
export function allCasesQueryOptions(
  client: ApiClient,
  params: AllCasesParams,
) {
  const config = params.config ?? CASE_CONFIG;
  const { filters, page, perPage } = params;

  const fetchCases = async (): Promise<AllCasesResult> => {
    const response = await client.post<
      Partial<SpiffPaginatedResponse<SpiffProcessInstanceRow>>
    >(
      "/process-instances",
      {
        report_metadata: buildCaseReportMetadata(filters, config, {
          scope: "all",
        }),
      },
      { query: { page, per_page: perPage } },
    );

    const { items, isApproximateTotal } = refineCases(
      (response.results ?? []).map((row) => toCaseListItem(row, config)),
      { statuses: filters.statuses },
    );

    return {
      items,
      pagination: response.pagination ?? EMPTY_PAGINATION,
      isApproximateTotal,
    };
  };

  return queryOptions({
    queryKey: qk.processInstances.all({
      keyword: filters.keyword,
      statuses: filters.statuses,
      processModelId: filters.processModelId,
      submittedFrom: filters.submittedFrom,
      submittedTo: filters.submittedTo,
      page,
      perPage,
    }),
    queryFn: fetchCases,
    staleTime: 30_000,
  });
}

export function useAllCases(
  params: AllCasesParams,
): UseQueryResult<AllCasesResult> {
  return useQuery(allCasesQueryOptions(useApiClient(), params));
}
